import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { CaretUpAshIcon, CaretDownAshIcon } from '@glu/icons-react';
import useStyles from './Menu.styles';
import Link from './Link';

const Menu = (props) => {
  const {
    children,
    className,
    CollapseIcon,
    dataQa,
    ExpandIcon,
    footer,
    LinkComponent,
    navLinks,
    NavLinksWrapper,
    SubMenuComponent,
    tier,
    openOnHover,
    toggleOpen,
    ...rest
  } = props;
  const [indexOfOpenSublink, setIndexOfOpenSublink] = useState(-1);
  const classes = useStyles(props);

  const toggleOpenSublink = index => setIndexOfOpenSublink(prev => (index === prev ? -1 : index));
  return (
    <li className={`${classes[`tier${tier}-item`]} ${classes.item} ${className}`}>
      <Link
        classes={classes}
        CollapseIcon={CollapseIcon}
        dataQa={dataQa ? `${dataQa}-tier-${tier}` : undefined}
        ExpandIcon={ExpandIcon}
        LinkComponent={LinkComponent}
        navLinks={navLinks}
        tier={tier}
        toggleOpen={toggleOpen}
        {...rest}
      >
        {children}
      </Link>
      {footer}
      {navLinks && (
      <div className={classes.navLinks}>
        { SubMenuComponent
          ? (
            <SubMenuComponent
              CollapseIcon={CollapseIcon}
              dataQa={dataQa}
              ExpandIcon={ExpandIcon}
              LinkComponent={LinkComponent}
              navLinks={navLinks}
              tier={tier + 1}
            />
          )
          : (
            <ul className={classes[`tier${tier}-children`]}>
              {navLinks.map((subLink, index) => (
                <Menu
                  openOnHover={openOnHover}
                  CollapseIcon={CollapseIcon}
                  dataQa={dataQa}
                  ExpandIcon={ExpandIcon}
                  key={`side-navigation-root-${index}-${tier + 1}`} // eslint-disable-line react/no-array-index-key
                  LinkComponent={LinkComponent}
                  navLinks={subLink.navLinks}
                  open={index === indexOfOpenSublink}
                  tier={tier + 1}
                  toggleOpen={() => toggleOpenSublink(index)}
                  {...subLink}
                  onClick={subLink.onClick}
                >
                  {subLink.children}
                </Menu>
              ))}
            </ul>
          )}
      </div>
      )}
    </li>
  );
};

Menu.propTypes = {
  /* Children of link component. Can take custom markup or component */
  children: PropTypes.node.isRequired,
  /** classname for adding custom styles to children and link */
  className: PropTypes.string,
  /** Icon indicating that the children nav items are open and can be closed */
  CollapseIcon: PropTypes.func,
  /** data attribute for easy targeting during qa testing */
  dataQa: PropTypes.string,
  /** toggle display of thin line to right of link */
  divider: PropTypes.bool,
  /** icon indicating children of this link can be expanded */
  ExpandIcon: PropTypes.func,
  /* Content that renders after the link. */
  footer: PropTypes.node,
  /** url target for the link */
  href: PropTypes.string,
  /** custom component when a tag or button is not desired */
  LinkComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /** custom component for sub menu to provide custom markup and styles */
  SubMenuComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /** array of objects with nav link data */
  navLinks: PropTypes.arrayOf(PropTypes.shape({
    /** typically a string, but can also be provided as a component */
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
    /** custom component to override the default sub menu component */
    menu: PropTypes.node,
    /** custom component rendered imediately after the children */
    footer: PropTypes.node,
    /** callback after clicked.  */
    onClick: PropTypes.func,
    /** used on top nav tabs. visual seperator of links. useful when links are images */
    divider: PropTypes.bool,
    /** display icon indicating child navLinks. required for setting styles correctly */
    hasIcon: PropTypes.bool,
    /** render children as plain markup instead of a button */
    isHeader: PropTypes.bool,
    /** toggle relative position of tabs so that sub menus can be styled */
    relativePosition: PropTypes.bool,
    /** toggle if tab receives background shading on hover.
     * typically used in combination with custom children components */
    shadeOnHover: PropTypes.bool,
    /** link href. not used if link is a parent of navLinks */
    href: PropTypes.string
  })),
  /** Component to wrap navLinks children. useful for custom styles */
  NavLinksWrapper: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /** callback when link is clicked */
  onClick: PropTypes.func,
  openOnHover: PropTypes.bool.isRequired,
  /** toggle relative position of the top tier link.
   * used to enable full width sub menus.
   */
  relativePosition: PropTypes.bool,
  /** toggle if tabs show hover */
  shadeOnHover: PropTypes.bool,
  /** level of nested link. useful for targeting child styles */
  tier: PropTypes.number,
  /** function for opening and closing child links  */
  toggleOpen: PropTypes.func
};
Menu.defaultProps = {
  className: '',
  CollapseIcon: CaretUpAshIcon,
  dataQa: undefined,
  divider: false,
  ExpandIcon: CaretDownAshIcon,
  relativePosition: true,
  footer: undefined,
  href: null,
  LinkComponent: 'a',
  navLinks: null,
  NavLinksWrapper: 'div',
  onClick: null,
  shadeOnHover: true,
  SubMenuComponent: undefined,
  tier: 0,
  toggleOpen: null
};

export default Menu;
